import { Button } from '@/components/Button/Button'
import { Input } from '@/components/Input/Input'
import { Loader } from '@/components/Loader/Loader'
import { Popup } from '@/components/Popup/Popup'
import { TextArea } from '@/components/TextArea/TextArea'
import { GeneratorFormContext } from '@/hooks/useGeneratorForm'
import { useToneState } from '@/state/tones'
import type { FunctionalComponent } from 'preact'
import { useContext, useState } from 'preact/hooks'

const placeholder = `e.g. Write casually, short sentences...

Or paste example posts:
"Finally fixed that bug..."
"Shipped at 2am..."`

export const ToneForm: FunctionalComponent<{
  type: 'create' | 'edit'
  onSuccess: () => void
  onCancel?: () => void
  onDelete?: () => void
}> = props => {
  const createTone = useToneState(s => s.create)
  const updateTone = useToneState(s => s.update)
  const [loading, setLoading] = useState(false)

  const form = useContext(GeneratorFormContext)!
  const tone = useToneState(s => s.list.find(item => item.id === form.toneId))

  const [name, setName] = useState(props.type === 'edit' ? (tone?.name ?? '') : '')
  const [content, setContent] = useState(props.type === 'edit' ? (tone?.content ?? '') : '')

  const submit = async () => {
    try {
      setLoading(true)
      let toneId: number
      if (props.type === 'create') {
        const data = await createTone(name, content)
        toneId = data.id
      }
      else {
        if (!form.toneId) throw new Error('Tone id myst be defined')
        const data = await updateTone(form.toneId, name, content)
        toneId = data.id
      }
      form.setToneId(toneId)
      props.onSuccess()
    }
    catch (e) {
      console.error(e)
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Popup.Title>
        {props.type === 'create' ? 'Create tone of voice' : 'Edit tone of voice'}
      </Popup.Title>

      <Popup.Fields>
        {props.type === 'create' && (
          <Popup.Msg>
            Define how AI should write your posts. Add style instructions, example posts you like,
            or describe your unique voice.
          </Popup.Msg>
        )}

        <Popup.Field label='Name'>
          <Input
            placeholder='My tone'
            disabled={loading}
            value={name}
            onChange={setName}
          />
        </Popup.Field>

        <Popup.Field label='Instructions'>
          <TextArea
            minRows={5}
            placeholder={placeholder}
            disabled={loading}
            value={content}
            onChange={setContent}
          />
        </Popup.Field>
      </Popup.Fields>

      <Popup.Footer>
        <Popup.Buttons>
          {props.type === 'edit' && tone && tone.id > -1 && (
            tone.defaultToneId !== null
              ? (
                <Button onClick={props.onDelete}>
                  Reset
                </Button>
              )
              : (
                <Button onClick={props.onDelete}>
                  Delete
                </Button>
              )
          )}
        </Popup.Buttons>

        <Popup.Buttons>
          {props.onCancel && (
            <Button
              onClick={props.onCancel}
            >
              Back
            </Button>
          )}
          <Button
            type='primary'
            disabled={loading || !name.trim() || !content.trim()}
            onClick={submit}
          >
            {props.type === 'edit' ? 'Save' : 'Create'}
            {loading && (
              <>
                {' '}
                <Loader />
              </>
            )}
          </Button>
        </Popup.Buttons>
      </Popup.Footer>
    </>
  )
}
